import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { MessageCircle, Eye, AlertTriangle, Users, Salad, Shield, Activity, ArrowRightLeft, Search, X } from 'lucide-react';
import { getInitials, formatDate } from '../../lib/utils';
import { ConsultationsOversight } from '../../components/admin/ConsultationsOversight';

const TIER_LABEL = { L1: 'Self-care', L2: 'Watch', L3: 'See a doctor', L4: 'Emergency' };
const TIER_COLOR = { L1: 'bg-emerald-100 text-emerald-800', L2: 'bg-amber-100 text-amber-800', L3: 'bg-orange-100 text-orange-800', L4: 'bg-rose-100 text-rose-800' };

function Metric({ icon: Icon, value, label, tone = 'text-primary', cardClass = '' }) {
  return (
    <Card className={`rounded-2xl ${cardClass}`}><CardContent className="pt-5 pb-4">
      <Icon className={`h-5 w-5 ${tone} mb-2`} />
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </CardContent></Card>
  );
}

export default function SoulAIAdmin() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [conversationDialog, setConversationDialog] = useState({ open: false, userId: null, userName: '' });
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get('/api/soulai/insights').then(res => setInsights(res.data)).finally(() => setLoading(false));
  }, []);

  const loadConversations = useCallback(async (q) => {
    setConvLoading(true);
    try {
      const res = await api.get('/api/soulai/conversations', { params: { search: q || undefined, limit: 200 } });
      setConversations(res.data.conversations || []);
    } catch { setConversations([]); } finally { setConvLoading(false); }
  }, []);

  // Debounced learner-wise search
  useEffect(() => {
    const t = setTimeout(() => loadConversations(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search, loadConversations]);

  const viewConversation = async (userId, userName) => {
    try {
      const res = await api.get(`/api/soulai/history?user_id=${userId}&limit=50`);
      setMessages(res.data.messages || []);
      setConversationDialog({ open: true, userId, userName });
    } catch { /* */ }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const tiers = insights?.tier_distribution || {};
  const tierEntries = ['L1', 'L2', 'L3', 'L4'].filter(t => tiers[t]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">SoulAI Insights</h1>
        <p className="text-muted-foreground mt-1">Monitor AI companion conversations, safety triage &amp; nutritionist consultations</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Metric icon={Users} value={insights?.total_users || 0} label="Active Learners" />
        <Metric icon={MessageCircle} value={insights?.total_messages || 0} label="Learner Messages" tone="text-blue-600" />
        <Metric icon={Activity} value={insights?.avg_messages_per_user || 0} label="Avg msgs / learner" tone="text-violet-600" />
        <Metric icon={AlertTriangle} value={insights?.emergencies || 0} label="Emergencies" tone="text-rose-600" cardClass="border-rose-200" />
        <Metric icon={Salad} value={insights?.nutrition_connects || 0} label="Nutritionist Connects" tone="text-emerald-600" cardClass="border-emerald-200" />
        <Metric icon={Shield} value={insights?.reports || 0} label="Reports" tone="text-amber-600" />
      </div>

      {/* Triage tier distribution */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><ArrowRightLeft className="h-4 w-4" />Safety triage breakdown</CardTitle>
          <CardDescription>How SoulAI classified its replies. L3/L4 are where it nudges to a doctor or emergency help.</CardDescription>
        </CardHeader>
        <CardContent>
          {tierEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No triage data yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tierEntries.map(t => (
                <span key={t} className={`px-3 py-1.5 rounded-full text-xs font-medium ${TIER_COLOR[t]}`} data-testid={`tier-${t}`}>
                  {t} · {TIER_LABEL[t]}: <strong>{tiers[t]}</strong>
                </span>
              ))}
              {(insights?.handoffs_total || 0) > 0 && (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                  Doctor handoffs: <strong>{insights.handoffs_confirmed || 0}/{insights.handoffs_total}</strong>
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversations — learner-searchable */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-xl">Conversations</CardTitle>
              <CardDescription>Search conversations learner-wise by name or email.</CardDescription>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search learner name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-8"
                data-testid="conversation-search-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="conversation-search-clear">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {convLoading ? (
            <p className="text-muted-foreground text-center py-8">Loading…</p>
          ) : conversations.length === 0 ? (
            <p className="text-muted-foreground text-center py-8" data-testid="conversations-empty">{search ? `No learners match "${search}"` : 'No conversations yet'}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Learner</TableHead>
                  <TableHead className="hidden md:table-cell">Last message</TableHead>
                  <TableHead className="text-center">Msgs</TableHead>
                  <TableHead className="hidden sm:table-cell">Last active</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversations.map(c => (
                  <TableRow key={c._id} data-testid="conversation-row">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(c.user_name)}</AvatarFallback></Avatar>
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-1">{c.user_name || 'Unknown'}
                            {c.emergencies > 0 && <Badge variant="outline" className="text-[9px] text-rose-700 border-rose-300 gap-0.5"><AlertTriangle className="h-2.5 w-2.5" />{c.emergencies}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.user_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-[240px] truncate text-sm text-muted-foreground">{c.last_message}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary">{c.user_message_count ?? c.count}</Badge></TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">{c.last_message_at ? formatDate(c.last_message_at) : '—'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => viewConversation(c._id, c.user_name)} data-testid={`view-conversation-${c._id}`}>
                        <Eye className="h-3 w-3 mr-1" />View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Nutritionist consultations — read-only oversight (moved here under SoulAI) */}
      <ConsultationsOversight />

      {/* Conversation transcript dialog */}
      <Dialog open={conversationDialog.open} onOpenChange={(o) => setConversationDialog({ open: o, userId: null, userName: '' })}>
        <DialogContent className="sm:max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Conversation — {conversationDialog.userName}</DialogTitle>
            <DialogDescription>Read-only SoulAI transcript for this learner.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-primary/10' : 'bg-muted'}`}>
                    <p className="text-[10px] text-muted-foreground mb-0.5">{m.role === 'user' ? 'Learner' : 'SoulAI'}</p>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
